// R98 — LALA ANSWERED WITH THREE DOTS, AND THAT IS WHAT A REFUSAL LOOKED LIKE ON THE MOST
// EXPENSIVE SURFACE IN THIS HOUSE.
//
// `POST /api/lala/chat` is sent from TWO places — the drawer and the Ask Lala page — and both typed
// the same line, 4,700 apart:
//
//     const data = await res.json().catch(() => ({}));
//     setMessages((m) => [...m, { role: "assistant", content: data.reply || "...", … }]);
//
// **`"..."` IS NOT A PLACEHOLDER. IT IS APPENDED TO THE THREAD AS A TURN FROM LALA**, under the
// member's own question, and it is what she is shown every time this route refuses in a way that
// carries no `reply`. The route composes a real sentence for each of those:
//
//   * **401** — "Sign in to talk with Lala." **The one she could fix in a single tap** (R61), and
//     the one a lapsed session hands her mid-conversation.
//   * **400** — "Say something to Lala."
//   * **500** — "Lala could not answer just now."
//
// Three sentences the server composed, none of which has ever reached a member (R51/R60/R66's shape,
// on the surface where a turn is paid for). And the failure is worse than silence, because silence
// at least reads as a failure: three dots read as **Lala having nothing to say to her.**
//
// **A `reply` THAT IS NOT A STRING IS NOT A REPLY** (R53's PRESENCE rule). Every 200 this route
// answers on a path it means carries one, including its own soft refusals ("Lala is resting.",
// "Lala is not connected yet.", the cooldown and the under-tier step) — so a 200 with no `reply` is
// a refusal wearing a success, and it is answered as one rather than as an ellipsis.
//
// **THE STOP BUTTON'S RULING IS KEPT AND MOVED IN HERE.** The page's own comment says *"A stop
// (abort) leaves the member's message in place so they can edit and resend"*, and nothing may be
// drawn for it. That was asked of `error instanceof DOMException && error.name === "AbortError"` at
// the call site; it is asked of the SIGNAL now (R97's argument one route over), because an exception
// name is a thing a polyfill or a test double can spell differently and a signal cannot.
//
// **HER WORDS STILL GO, AND THE SENTENCE IS WHAT CHANGES** — R57's ruling, kept by R62 one route
// over. Nothing here refuses to send.
//
// `doFetch` is a PARAMETER (R46), so every answer this route can give is reachable with no browser,
// no session and no engine. This module imports NOTHING (the entry requirement for a
// `scripts/`-loadable law).

export const lalaChatPath = "/api/lala/chat";

// ===========================================================================================
// E5 (2026-08-11, HER RULING) — **SHE MAY NOT SAY SHE CANNOT SPEAK AND THEN SPEAK.**
//
// Her report: *"lala says 'lala cant speak try again' and then responds right after."* Measured, the
// pair is not one response — it is TWO REQUESTS, and the client renders a turn for each.
//
//   * **The Ask Lala PAGE had NO in-flight guard at all.** `doSend` ran on every submit.
//   * **The DRAWER's guard was `sending`, a `useState`** — which is D-5's defect verbatim, one
//     surface over and on the most expensive one in this house: a state guard reads the value from
//     the render that BOUND the handler, so two submits inside one frame both pass. Key auto-repeat
//     and a double tap on a slow phone both land inside one frame. **It passes every presence check
//     there is and still sends twice.**
//
// So one send, one law, THREE outcomes rather than two (R18): *nothing to send* and *one is already
// going* are different facts about the same tap. **The ORDER is load-bearing and is held as an
// order** — asked after the empty test, it never fires on the press that matters, because the text
// is always still there. This is `commentSendState`'s shape exactly, and it is deliberately the same
// shape: two send laws in one house drift, and a drawer that double-sends while a page does not is
// the report she just made.
export type LalaSendVerdict = "empty" | "in-flight" | "send";
export function lalaSendState(
  { text, attachments = 0, inFlight = false }: { text: string; attachments?: number; inFlight?: boolean },
): LalaSendVerdict {
  if (!String(text || "").trim() && !(Number(attachments) > 0)) return "empty";
  if (inFlight) return "in-flight";
  return "send";
}

// THE ROOM'S OWN WORDS, DECLARED ONCE. The two surfaces had drifted to two spellings of one
// sentence (R56/R60), and the longer one wins because it says what to do next. English, and
// deliberately not a catalog row: 14-B6's ruling is that a lone translated line over an
// untranslated screen is the defect, and the Ask Lala surfaces are still English by carried flag.
export const LALA_SEND_COPY = {
  failed: "Lala could not answer just now. Please try again.",
};

export type LalaSendPayload = {
  reply: string;
  seal?: unknown;
  sealOptions?: unknown;
  cards?: unknown;
  // ITEM 3c: the product manual, handed over in the conversation as a real file.
  manual?: unknown;
  // PDF-1: the member's own work, compiled into one document and handed over the same way.
  compilation?: unknown;
  lock?: unknown;
  turns?: unknown;
  // FUNNEL-4: present only on a turn that really charged one of her five.
  freeTurns?: unknown;
};

// The FIVE payloads a turn can carry are typed by whoever RENDERS them (the seal card, the choice
// list, the card images, the manual handoff and PDF-1's compilation all live in components), so they
// ride through as parameters rather than being named a second time here. `unknown` by default, which
// is what a gate drives it with.
export type LalaSendAnswer<S = unknown, O = unknown, C = unknown, M = unknown, P = unknown> = {
  kind: "ok" | "sign-in" | "refused" | "offline" | "aborted";
  // What the server ACTUALLY sent, or "" when it sent nothing usable. Never an ellipsis, and never
  // the room's fallback: the fallback is chosen by `lalaSendTurn`, so a caller cannot mistake the
  // house's own sentence for one Lala wrote.
  reply: string;
  seal: S | undefined;
  sealOptions: O | undefined;
  cards: C | undefined;
  // ITEM 3c. Present ONLY on a turn that really delivered the file: the SERVER decides membership,
  // and a client that drew a download link out of its own reasoning would be offering something the
  // door would then refuse.
  manual: M | undefined;
  // PDF-1. Present ONLY on a turn that really compiled a document AND really wrote its ledger row:
  // the SERVER decides both, and a client that drew a download link out of its own reasoning would be
  // offering a file no row stands behind.
  compilation: P | undefined;
  // The lock and the turn count ride ONLY on an answer that landed (T7-5/W2-3). A refusal carries
  // neither, and a client that raised a box or moved a counter from one would be deciding a door
  // and a budget it is only ever told about (R20/T8-2).
  lock: unknown;
  turns: unknown;
  // FUNNEL-4 — what is LEFT of her one allotment, as the server counted it. It rides the same way
  // `turns` does and for the same reason: a browser that kept its own tally would hand back a sixth
  // turn on a refresh, which is her clause 4's whole worry. Absent on every other member's answer.
  freeTurns: unknown;
  // The server's own sentence, where it sent one.
  message: string;
};

const empty = <S, O, C, M, P>(kind: LalaSendAnswer["kind"], message = ""): LalaSendAnswer<S, O, C, M, P> => ({
  kind, reply: "", seal: undefined, sealOptions: undefined, cards: undefined, manual: undefined, compilation: undefined, lock: undefined, turns: undefined, freeTurns: undefined, message,
});

export type LalaSendBody = {
  message: string;
  conversationId?: number | string | null;
  attachmentIds?: unknown[];
  // T5's handoff block, whose shape belongs to `src/askLalaHandoff.js` and is not named a second
  // time here (R13/R14: a second copy of a shape is a second answer key).
  handoff?: unknown;
  // ITEM 9 — which door this turn came through, so a ticket filed in the turn carries its own
  // provenance on her sheet. The SERVER refuses anything off the roster (R141), so this is a hint
  // it may trust rather than a fact it takes.
  surface?: "lala-drawer" | "lala-page";
  signal?: AbortSignal;
};

export async function sendToLala<S = unknown, O = unknown, C = unknown, M = unknown, P = unknown>(
  body: LalaSendBody,
  doFetch: typeof fetch = typeof fetch === "function" ? fetch : (undefined as never),
): Promise<LalaSendAnswer<S, O, C, M, P>> {
  const { signal, ...payload } = body;
  let response: Response;
  try {
    response = await doFetch(lalaChatPath, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      ...(signal ? { signal } : {}),
    });
  } catch {
    if (signal?.aborted) return empty<S, O, C, M, P>("aborted");
    return empty<S, O, C, M, P>("offline");
  }
  const data = await response.json().catch(() => null) as (Partial<LalaSendPayload> & { message?: unknown }) | null;
  const said = typeof data?.message === "string" ? data.message : "";
  // 401 is its own answer because it is the only one with a door in it (R61): every other refusal
  // asks her to try again, and this one asks her to sign in, which is one tap.
  if (response.status === 401) return empty<S, O, C, M, P>("sign-in", said);
  // PRESENCE, never truthiness (R53): a 200 with no `reply` STRING is a refusal wearing a success,
  // which is exactly what the ellipsis used to draw over.
  if (!response.ok || !data || typeof data.reply !== "string" || !data.reply.trim()) {
    return empty<S, O, C, M, P>("refused", said);
  }
  return {
    kind: "ok",
    reply: data.reply,
    seal: data.seal as S | undefined,
    sealOptions: data.sealOptions as O | undefined,
    cards: data.cards as C | undefined,
    manual: data.manual as M | undefined,
    compilation: data.compilation as P | undefined,
    lock: data.lock,
    turns: data.turns,
    freeTurns: data.freeTurns,
    message: "",
  };
}

// WHAT GOES IN THE THREAD. An abort draws NOTHING (the stop button's ruling); everything else draws
// exactly one turn, and a refusal draws the SERVER'S OWN words wherever it sent any.
// ===========================================================================================
// IT #300 — **SHE WAS TOLD LALA COULD NOT ANSWER, AND THE ANSWER WAS ALREADY BEING WRITTEN.**
//
// Her report: *"Lala keeps saying she can't answer right now, and when I leave and come back she
// has my answer."* Measured: her turn started 01:33:28Z and its answer was stored 01:36:32Z. A phone
// that locks or switches app mid-turn drops the request, `fetch` throws, and the room said the
// failure sentence while the server kept going and saved the reply (1,551 dropped turns in 14 days).
// So a dropped request (`offline`, never `refused` and never `aborted`) re-reads the thread for the
// reply to HER words, among rows newer than the ones she already had, before it says anything.
// ===========================================================================================
export const DROPPED_REPLY_TRIES = 21;
export const DROPPED_REPLY_WAIT_MS = 10_000;

type StoredTurn = { id?: string | number; role: string; content: string };

/** The newest STORED message id on screen (0 when none), taken before her turn is drawn. */
export function newestStoredId(messages: ReadonlyArray<{ id?: string | number }> | null | undefined): number {
  let max = 0;
  for (const m of Array.isArray(messages) ? messages : []) {
    const id = Number(m?.id);
    if (Number.isSafeInteger(id) && id > max) max = id;
  }
  return max;
}

/**
 * Lala's stored reply to what she just sent, or null. Only rows NEWER than `afterId` are read, so an
 * older answer to the same words is never handed back as this one; her own words decide which reply
 * is hers, and a user row with no reply after it yet is not an answer.
 */
export function recoveredReply<M extends StoredTurn>(
  thread: ReadonlyArray<M> | null | undefined, sent: string, afterId: number,
  // A stored row can carry the house's attachment note after her words; the caller hands in the ONE
  // stripper (`stripAttachNote`) rather than this file spelling it a second time.
  normalise: (text: string) => string = (text) => String(text || "").trim(),
): M | null {
  if (!Array.isArray(thread)) return null;
  const want = normalise(String(sent || ""));
  if (!want) return null;
  for (let i = thread.length - 2; i >= 0; i -= 1) {
    const m = thread[i];
    const id = Number(m?.id);
    if (!Number.isSafeInteger(id) || id <= afterId) return null;
    if (m.role === "user" && normalise(String(m.content || "")) === want) {
      const next = thread[i + 1];
      return next && next.role === "assistant" && String(next.content || "").trim() ? next : null;
    }
  }
  return null;
}

/** Re-reads the thread until her reply is there, or the tries run out. The first read is immediate. */
export async function awaitDroppedReply<M extends StoredTurn>(
  read: () => Promise<ReadonlyArray<M> | null>,
  sent: string,
  afterId: number,
  { tries = DROPPED_REPLY_TRIES, waitMs = DROPPED_REPLY_WAIT_MS, sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms)), normalise }:
    { tries?: number; waitMs?: number; sleep?: (ms: number) => Promise<void>; normalise?: (text: string) => string } = {},
): Promise<M | null> {
  for (let i = 0; i < tries; i += 1) {
    const found = recoveredReply(await read().catch(() => null), sent, afterId, normalise);
    if (found) return found;
    if (i < tries - 1) await sleep(waitMs);
  }
  return null;
}

export function lalaSendTurn(a: LalaSendAnswer<unknown, unknown, unknown>, failed = LALA_SEND_COPY.failed): string {
  if (a.kind === "aborted") return "";
  if (a.kind === "ok") return a.reply;
  return a.message || failed;
}
